import React from 'react'
import {Image} from '@/components/image'
import useMediaQuery from 'hec/hooks/useMediaQuery'
import {useMediaQueryWithBuilder} from 'hec/builder/hooks/useMediaQueryWithBuilder'
import {TextBlock} from '@/components/text-block'
import {getHeroBgStyle} from '../utils'
import {ITwoColumnProps} from '../types'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    ContentButtonNavigation
} from '@/components/ui/carousel'

const TwoColumn = (props: ITwoColumnProps) => {
    const {isMobile: isBuilderMobile} = useMediaQueryWithBuilder()
    const isMobile = useMediaQuery('sm')
    const {
        backgroundType,
        backgroundImage,
        backgroundImageMobile,
        backgroundColor,
        backgroundGradient,
        heroImage,
        mobileImage,
        external,
        external_link,
        link,
        couponCode,
        enableCouponCode,
        eyebrowText,
        eyebrowIcon,
        eyebrowType,
        disclaimerText,
        headlineType,
        headlineImage,
        headlineText,
        textBlock,
        callToAction,
        buttonColor,
        enableOverlay,
        overlayColor,
        contentPosition,
        textAlignment,
        eyebrowBackgroundColor,
        eyebrowSize,
        eyebrowTextColor,
        carouselItems = [],
        carouselAutoplay,
        carouselAutoplayDelay,
        carouselDots
    } = props
    const resolvedLink = external
        ? external_link?.value?.data?.url ?? ''
        : link?.value?.data?.url ?? ''
    resolvedLink == '' && console.error('No link provided for TwoColumn component')

    // Support for mobile-specific background image
    const bgStyle = getHeroBgStyle({
        backgroundType,
        backgroundImage,
        backgroundImageMobile,
        backgroundColor,
        backgroundGradient,
        isMobile,
        isBuilderMobile
    })

    return backgroundType === 'carousel' ? (
        <Carousel
            opts={{
                align: 'start',
                loop: true
            }}
            autoplay={carouselAutoplay}
            autoplayDelay={carouselAutoplayDelay || 4000}
            stopOnInteraction={true}
            className="w-full"
        >
            <CarouselContent>
                {carouselItems.map((item, index) => {
                    const carouselBgStyle = getHeroBgStyle({
                        backgroundType: item.carouselBackgroundType || 'image',
                        backgroundImage: item.backgroundImage,
                        backgroundImageMobile: item.backgroundImageMobile,
                        backgroundColor: item.backgroundColor,
                        backgroundGradient: item.backgroundGradient,
                        isMobile,
                        isBuilderMobile
                    })

                    const carouselResolvedLink = item.external
                        ? item.external_link?.value?.data?.url ?? ''
                        : item.link?.value?.data?.url ?? ''

                    return (
                        <CarouselItem key={index}>
                            <div
                                className="hero hero-two-col flex flex-col relative pb-8 pt-8 justify-center w-full"
                                style={carouselBgStyle}
                            >
                                {!isMobile && !isBuilderMobile ? (
                                    <div
                                        className={`grid grid-cols-12 gap-2 lg:gap-10  w-full  justify-center items-center p-4 ${
                                            item.contentPosition === 'right'
                                                ? 'flex-row-reverse'
                                                : 'flex-row'
                                        }`}
                                        style={{display: 'flex'}}
                                    >
                                        <div className="col-span-6 flex justify-center">
                                            <Image
                                                src={item.heroImage || ''}
                                                alt="Left Image"
                                                className=" max-w-[350px] lg:max-w-[480px]  xl:max-w-[700px]"
                                            />
                                        </div>
                                        <div className="col-span-6 flex flex-col justify-center items-center text-center gap-2 lg:gap-4">
                                            <div className="flex flex-col justify-center items-center text-center">
                                                <TextBlock
                                                    couponCode={item.couponCode}
                                                    enableCouponCode={item.enableCouponCode}
                                                    eyebrow={item.eyebrowText}
                                                    eyebrowIcon={item.eyebrowIcon}
                                                    eyebrowType={item.eyebrowType}
                                                    disclaimer={
                                                        item.disclaimerText
                                                            ? item.disclaimerText
                                                            : ''
                                                    }
                                                    headlineType={item.headlineType}
                                                    headlineImage={item.headlineImage}
                                                    headline={
                                                        item.headlineText ? item.headlineText : ''
                                                    }
                                                    supportText={
                                                        item.textBlock ? item.textBlock : ''
                                                    }
                                                    cta={item.callToAction ? item.callToAction : ''}
                                                    external={item.external}
                                                    link={carouselResolvedLink}
                                                    overlay={item.enableOverlay}
                                                    overlayColor={item.overlayColor}
                                                    alignment={item.textAlignment}
                                                    buttonColor={item.buttonColor}
                                                    eyebrowBackgroundColor={
                                                        item.eyebrowBackgroundColor
                                                    }
                                                    eyebrowSize={item.eyebrowSize}
                                                    eyebrowTextColor={item.eyebrowTextColor}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className={`flex flex-col md:hidden items-center text-center px-4 gap-2 ${
                                            item.contentPosition === 'right'
                                                ? 'flex-col-reverse'
                                                : ''
                                        }`}
                                        style={carouselBgStyle}
                                    >
                                        <Image
                                            src={item.mobileImage || ''}
                                            alt="Left Image"
                                            className={`$${
                                                item.contentPosition === 'right' ? 'mt-4' : 'mb-4'
                                            } w-full max-w-xs`}
                                        />
                                        <TextBlock
                                            couponCode={item.couponCode}
                                            enableCouponCode={item.enableCouponCode}
                                            eyebrow={item.eyebrowText}
                                            eyebrowIcon={item.eyebrowIcon}
                                            eyebrowType={item.eyebrowType}
                                            disclaimer={
                                                item.disclaimerText ? item.disclaimerText : ''
                                            }
                                            headlineType={item.headlineType}
                                            headlineImage={item.headlineImage}
                                            headline={item.headlineText ? item.headlineText : ''}
                                            supportText={item.textBlock ? item.textBlock : ''}
                                            cta={item.callToAction ? item.callToAction : ''}
                                            external={item.external}
                                            link={carouselResolvedLink}
                                            overlay={item.enableOverlay}
                                            overlayColor={item.overlayColor}
                                            buttonColor={item.buttonColor}
                                            alignment={item.textAlignment}
                                        />
                                    </div>
                                )}
                            </div>
                        </CarouselItem>
                    )
                })}
            </CarouselContent>
            {carouselItems.length > 1 && carouselDots !== false && (
                <ContentButtonNavigation
                    buttonType="circleButton"
                    data-testid={'twocolumn-hero-carousel'}
                />
            )}
        </Carousel>
    ) : (
        <div
            className="hero hero-two-col flex flex-col relative pb-8 pt-8 justify-center w-full"
            style={bgStyle}
        >
            {!isMobile && !isBuilderMobile ? (
                <div
                    className={`grid grid-cols-12 gap-2 lg:gap-10  w-full  justify-center items-center px-8 py-4 lg:p-4 ${
                        contentPosition === 'right' ? 'flex-row-reverse' : 'flex-row'
                    }`}
                    style={{display: 'flex'}}
                >
                    <div className="col-span-6 flex justify-center">
                        <Image
                            src={heroImage || ''}
                            alt="Left Image"
                            className=" max-w-[280px] lg:max-w-[480px] xl:max-w-[700px]"
                        />
                    </div>
                    <div className="col-span-6 flex flex-col justify-center items-center text-center gap-2 lg:gap-4">
                        <div className="flex flex-col justify-center items-center text-center">
                            <TextBlock
                                couponCode={couponCode}
                                enableCouponCode={enableCouponCode}
                                eyebrow={eyebrowText}
                                eyebrowIcon={eyebrowIcon}
                                eyebrowType={eyebrowType}
                                disclaimer={disclaimerText ? disclaimerText : ''}
                                headlineType={headlineType}
                                headlineImage={headlineImage}
                                headline={headlineText ? headlineText : ''}
                                supportText={textBlock ? textBlock : ''}
                                cta={callToAction ? callToAction : ''}
                                external={external}
                                link={resolvedLink}
                                overlay={enableOverlay}
                                overlayColor={overlayColor}
                                alignment={textAlignment}
                                buttonColor={buttonColor}
                                eyebrowBackgroundColor={eyebrowBackgroundColor}
                                eyebrowSize={eyebrowSize}
                                eyebrowTextColor={eyebrowTextColor}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={`flex flex-col md:hidden items-center text-center px-4 gap-2 ${
                        contentPosition === 'right' ? 'flex-col-reverse' : ''
                    }`}
                    style={bgStyle}
                >
                    <Image
                        src={mobileImage || ''}
                        alt="Left Image"
                        className={`$${
                            contentPosition === 'right' ? 'mt-4' : 'mb-4'
                        } w-full max-w-xs`}
                    />
                    <TextBlock
                        couponCode={couponCode}
                        enableCouponCode={enableCouponCode}
                        eyebrow={eyebrowText}
                        eyebrowIcon={eyebrowIcon}
                        eyebrowType={eyebrowType}
                        disclaimer={disclaimerText ? disclaimerText : ''}
                        headlineType={headlineType}
                        headlineImage={headlineImage}
                        headline={headlineText ? headlineText : ''}
                        supportText={textBlock ? textBlock : ''}
                        cta={callToAction ? callToAction : ''}
                        external={external}
                        link={resolvedLink}
                        overlay={enableOverlay}
                        overlayColor={overlayColor}
                        buttonColor={buttonColor}
                        alignment={textAlignment}
                    />
                </div>
            )}
        </div>
    )
}

export default TwoColumn
